import React, { useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  Boxes,
  CircleDollarSign,
  Download,
  Eye,
  Filter,
  Package,
  PackageMinus,
  PackagePlus,
  Pencil,
  Plus,
  Save,
  Send,
  Trash2,
  Truck,
} from "lucide-react";
import { ImportarEstoque } from "./importar";
import { Inteligencia } from "./inteligencia";
import { Barra, Btn, Busca, Dado, Field, Modal, Pill, Stat, Vazio, usarServidor } from "../ui/componentes";
import { PED_ROT, PED_TOM, brl, dias, fdata } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   ESTOQUE
   ══════════════════════════════════════════════════════════════════ */
function Estoque({ db, set, toast, usuario, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [aba, setAba] = useState("pecas");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("todas");
  const [soBaixas, setSoBaixas] = useState(false);
  const [movModal, setMovModal] = useState(null);
  const [nova, setNova] = useState(false);
  const [verId, setVerId] = useState(null);   // peça aberta no detalhe
  const [editando, setEditando] = useState(null); // id em edição
  const [excluindo, setExcluindo] = useState(false);
  const [importar, setImportar] = useState(false);
  const [mf, setMf] = useState({ qtd: 1, custo: 0, motivo: "", forn: "" });
  const VAZIO_PECA = { cod: "", nome: "", catId: "", fornId: "", marca: "", un: "UN", qtd: 0, min: 2, max: 10, custo: 0, venda: 0, local: "", ncm: "" };
  const [pf, setPf] = useState(VAZIO_PECA);

  /**
   * Criar categoria daqui mesmo.
   *
   * A lista de categorias era derivada das peças já cadastradas, então numa
   * oficina sem peça nenhuma ela nascia vazia e não havia como criar a
   * primeira — o campo era um select sem opção. E `categoria_id` nem era
   * enviado no cadastro, então a escolha não saía do navegador.
   */
  const [novaCat, setNovaCat] = useState("");
  const [criandoCat, setCriandoCat] = useState(false);
  const [novoForn, setNovoForn] = useState("");
  const [criandoForn, setCriandoForn] = useState(false);
  const [confirmando, setConfirmando] = useState(false); // pedido de compra
  const [pedido, setPedido] = useState(null);            // pedido aberto
  const [recebendo, setRecebendo] = useState(false);
  const criarCategoria = async () => {
    const nome = novaCat.trim();
    if (!nome) return;
    setCriandoCat(true);
    try {
      const c = await acoes.criarCategoria(nome);
      setNovaCat("");
      setPf((f) => ({ ...f, catId: c.id }));
      toast("Categoria criada", `"${c.nome}" já pode ser usada nas peças.`);
    } catch (e) {
      toast("Não deu para criar", e?.message ?? "Tente de novo.", "erro");
    } finally { setCriandoCat(false); }
  };
  // "proprietario" é quem abriu a oficina — mesmo poder que admin. Sem
  // incluir aqui, o dono não enxergava importação, margem nem exclusão,
  // e o servidor aceitaria as três (exigirPapel já trata os dois iguais).
  const criarFornecedor = async () => {
    const nome = novoForn.trim();
    if (!nome) return;
    setCriandoForn(true);
    try {
      const f = await acoes.criarFornecedor(nome);
      setNovoForn("");
      setPf((x) => ({ ...x, fornId: f.id }));
      toast("Fornecedor criado", `"${f.nome}" já pode ser ligado às peças.`);
    } catch (e) {
      toast("Não deu para criar", e?.message ?? "Tente de novo.", "erro");
    } finally { setCriandoForn(false); }
  };

  const admin = ["admin", "proprietario"].includes(usuario.papel);

  // Das categorias cadastradas, não das peças: senão a lista nasce vazia numa
  // oficina nova, e categoria sem peça alguma nunca apareceria.
  const cats = ["todas", ...(db.categorias ?? []).map((c) => c.nome)];
  const baixas = db.pecas.filter((p) => p.qtd <= p.min);
  const valorEstoque = db.pecas.reduce((s, p) => s + p.qtd * p.custo, 0);
  const valorVenda = db.pecas.reduce((s, p) => s + p.qtd * p.venda, 0);
  // Contava a lista inteira, que o servidor cortava em 200. Sem o corte,
  // viraria o total de todos os meses.
  const movNoMes = db.mov.filter((m) => m.data.slice(0, 7) === dias(0).slice(0, 7)).length;

  const lista = db.pecas.filter((p) => {
    if (cat !== "todas" && p.cat !== cat) return false;
    if (soBaixas && p.qtd > p.min) return false;
    const t = q.toLowerCase();
    return !t || (p.nome + p.cod + p.marca + p.local + p.forn).toLowerCase().includes(t);
  });

  const abrirMov = (peca, tipo) => { setMf({ qtd: 1, custo: peca.custo, motivo: tipo === "entrada" ? "" : "Uso interno", forn: peca.forn }); setMovModal({ peca, tipo }); };

  /**
   * Entrada e saída viram movimento no razão do servidor.
   *
   * O custo médio é recalculado lá, por gatilho — fazer a conta aqui daria
   * número diferente do razão na primeira concorrência, e aí ninguém sabe
   * mais qual dos dois está certo.
   */
  const gravarMov = () => {
    const { peca, tipo } = movModal;
    const qtd = +mf.qtd || 0;
    if (qtd <= 0) { toast("Quantidade inválida", "Informe quantas unidades entraram ou saíram.", "erro"); return; }

    executar(
      () => tipo === "entrada"
        ? acoes.entradaEstoque({ pecaId: peca.id, qtd, custo: +mf.custo || peca.custo,
                                 documento: mf.forn })
        // Saída manual é ajuste de inventário: a contagem passa a ser a
        // realidade e a diferença fica registrada, que é o que o contador
        // vai querer ver depois.
        : acoes.ajustarInventario(peca.id, Math.max(0, peca.fisico - qtd), mf.motivo || "Baixa manual"),
      () => {
        setMovModal(null);
        toast(tipo === "entrada" ? "Entrada lançada" : "Baixa registrada",
          tipo === "entrada"
            ? `${qtd} ${peca.un} de ${peca.nome}. Custo médio atualizado e conta a pagar gerada.`
            : `${qtd} ${peca.un} de ${peca.nome} saíram do estoque.`);
      },
      "Não deu para lançar");
  };

  /* Guardo o id, não o objeto: depois de salvar ou dar entrada a lista é
     recarregada e o objeto antigo ficaria congelado, mostrando o estoque de
     antes do movimento. */
  const peca = db.pecas.find((x) => x.id === verId) ?? null;

  const fecharForm = () => { setNova(false); setEditando(null); setPf(VAZIO_PECA); };

  const abrirEdicao = (p) => {
    setPf({ cod: p.cod, nome: p.nome, catId: p.catId ?? "", fornId: p.fornId ?? "",
            marca: p.marca, un: p.un, qtd: p.qtd, min: p.min, max: p.max,
            custo: p.custo, venda: p.venda, local: p.local, ncm: p.ncm ?? "" });
    setEditando(p.id);
  };

  const confirmarExclusaoPeca = () => executar(
    () => acoes.excluirPeca(peca.id),
    () => { setExcluindo(false); setVerId(null);
            toast("Peça excluída", `${peca.nome} saiu da lista. As OS antigas que a usaram continuam intactas.`); },
    "Não deu para excluir");

  const salvarPeca = () => executar(
    async () => {
      if (!pf.nome.trim() || !pf.cod.trim()) {
        throw new Error("A peça precisa de código e nome para ser localizada depois.");
      }
      if (editando) { await acoes.salvarPeca(editando, pf); return null; }
      const p = await acoes.criarPeca(pf);
      // A peça nasce com estoque zero: quantidade só muda por movimento, e
      // é isso que mantém o razão batendo com a prateleira.
      if (+pf.qtd > 0) {
        await acoes.entradaEstoque({ pecaId: p.id, qtd: +pf.qtd, custo: +pf.custo,
                                     documento: "Cadastro inicial" });
      }
      return p;
    },
    () => {
      const eraEdicao = !!editando;
      fecharForm();
      toast(eraEdicao ? "Peça atualizada" : "Peça cadastrada",
        eraEdicao ? `${pf.nome} salva.` : `${pf.nome} disponível para orçamentos.`);
    },
    editando ? "Não deu para salvar" : "Não deu para cadastrar");

  /* Quanto comprar: subir até o máximo. Sem máximo definido, o dobro do
     mínimo — melhor um palpite explícito do que a conta dar negativo, que
     é o que acontecia quando `max` ficava em zero. */
  const aComprar = (p) => Math.max(1, Math.ceil((p.max || p.min * 2) - p.qtd));

  /* Um pedido por fornecedor. Peça sem fornecedor cai num pedido avulso, que
     a oficina completa na mão — melhor do que sumir da sugestão. */
  const grupos = (() => {
    const m = new Map();
    for (const p of baixas) {
      const k = p.fornId ?? "";
      if (!m.has(k)) m.set(k, { fornecedorId: p.fornId ?? null, forn: p.forn || "Sem fornecedor", itens: [] });
      m.get(k).itens.push({ ...p, comprar: aComprar(p) });
    }
    return [...m.values()];
  })();
  const totalCompra = grupos.reduce(
    (s, g) => s + g.itens.reduce((t, i) => t + i.comprar * i.custo, 0), 0);

  const gerarPedido = () => executar(
    () => acoes.gerarPedidos(grupos),
    (criados) => {
      setConfirmando(false);
      setAba("pedidos");
      toast("Pedido de compra gerado",
        `${criados.length} pedido(s) em rascunho, ${brl(totalCompra)}. Nada foi enviado ainda — confira e marque como enviado.`);
    },
    "Não deu para gerar o pedido");

  const abrirPedido = (p) => executar(
    () => acoes.verPedido(p.id),
    (cheio) => setPedido({ ...p, linhas: cheio?.itens ?? [] }),
    "Não deu para abrir o pedido");

  const mudarPedido = (p, status, msg) => executar(
    () => acoes.marcarPedido(p.id, status),
    () => { setPedido(null); toast(msg, `Pedido ${p.numero} — ${p.forn}.`); },
    "Não deu para mudar o pedido");

  const receberPedido = () => executar(
    () => acoes.receberPedido(pedido.id),
    () => { setRecebendo(false); setPedido(null);
            toast("Mercadoria recebida",
              `Pedido ${pedido.numero} deu entrada no estoque. Custo médio atualizado e conta a pagar gerada.`); },
    "Não deu para receber");

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label="Itens cadastrados" value={db.pecas.length} icon={Boxes} sub={`${db.pecas.reduce((s, p) => s + p.qtd, 0)} unidades em prateleira`} />
        <Stat label="Capital parado (custo)" value={brl(valorEstoque)} icon={CircleDollarSign} sub={`${brl(valorVenda)} a preço de venda`} />
        <Stat label="Abaixo do mínimo" value={baixas.length} tone={baixas.length ? "signal" : "ok"} icon={AlertTriangle} sub={baixas.length ? "repor antes de faltar na bancada" : "nenhuma reposição urgente"} />
        <Stat label="Movimentações no mês" value={movNoMes} icon={ArrowRightLeft} sub="entradas e saídas" />
      </div>

      {baixas.length > 0 && (
        <div className="card p-4" style={{ borderLeft: "3px solid var(--signal)" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2"><AlertTriangle size={15} className="t-signal" /><span className="dsp" style={{ fontSize: 15 }}>Sugestão de reposição</span></div>
            {/* PEDIDO DE COMPRA DESLIGADO POR ORA — decisão da oficina.
                A reposição segue manual: esta lista diz o que falta e quem é
                o fornecedor, e a entrada da mercadoria é lançada peça a peça
                pelo botão Entrada, que sempre funcionou.

                Nada foi apagado. As rotas /pedidos continuam no servidor e o
                resto da tela está logo abaixo neste arquivo. Para religar,
                basta devolver este botão e a aba — foi só isso que saiu. */}
            <span className="text-xs t-mut2">reposição lançada à mão, pela Entrada de cada peça</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead><tr><th>Peça</th><th>Fornecedor</th><th className="num">Em estoque</th><th className="num">Mínimo</th><th className="num">Comprar</th><th className="num">Custo estimado</th></tr></thead>
              <tbody>
                {baixas.map((p) => (
                  <tr key={p.id}>
                    <td><span className="text-sm font-medium">{p.nome}</span><span className="block text-xs t-mut2 mono">{p.cod}</span></td>
                    <td className="text-sm t-mut">{p.forn || <span className="t-mut2">sem fornecedor</span>}</td>
                    <td className="num text-sm"><span className={p.qtd === 0 ? "t-crit font-semibold" : "t-signal font-semibold"}>{p.qtd}</span></td>
                    <td className="num text-sm t-mut">{p.min}</td>
                    <td className="num text-sm font-semibold">{aComprar(p)} {p.un}</td>
                    <td className="num text-sm">{brl(aComprar(p) * p.custo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-1 scroll" style={{ overflowX: "auto", borderBottom: "1px solid var(--line)" }}>
        <button className={`tab ${aba === "pecas" ? "on" : ""}`} onClick={() => setAba("pecas")}>Peças <span className="mono t-mut2">{db.pecas.length}</span></button>
        <button className={`tab ${aba === "mov" ? "on" : ""}`} onClick={() => setAba("mov")}>Movimentações <span className="mono t-mut2">{db.mov.length}</span></button>
        {admin && <button className={`tab ${aba === "int" ? "on" : ""}`} onClick={() => setAba("int")}>Onde está o dinheiro</button>}
      </div>

      {aba === "int" ? <Inteligencia db={db} set={set} toast={toast} acoes={acoes} />
       : aba === "pecas" ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Busca v={q} set={setQ} ph="Buscar por nome, código, marca, prateleira…" />
            <select className="inp" style={{ width: 168 }} value={cat} onChange={(e) => setCat(e.target.value)}>
              {cats.map((c) => <option key={c} value={c}>{c === "todas" ? "Todas as categorias" : c}</option>)}
            </select>
            <Btn variant={soBaixas ? "s" : "o"} icon={Filter} onClick={() => setSoBaixas(!soBaixas)}>Só as que faltam</Btn>
            {admin && <Btn variant="o" icon={Download} onClick={() => setImportar(true)}>Importar</Btn>}
            <Btn variant="p" icon={Plus} onClick={() => setNova(true)}>Nova peça</Btn>
          </div>
          <div className="card" style={{ overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table className="tbl">
                <thead><tr><th>Código</th><th>Peça</th><th>Local</th><th className="num">Estoque</th><th className="num">Custo</th><th className="num">Venda</th>{admin && <th className="num">Margem</th>}<th></th></tr></thead>
                <tbody>
                  {lista.map((p) => {
                    const tone = p.qtd === 0 ? "crit" : p.qtd <= p.min ? "signal" : "ok";
                    const margem = p.venda ? ((p.venda - p.custo) / p.venda) * 100 : 0;
                    return (
                      <tr key={p.id} onClick={() => setVerId(p.id)} style={{ cursor: "pointer" }}
                        title="Abrir a ficha da peça">
                        <td className="mono text-xs t-mut">{p.cod}</td>
                        <td><span className="text-sm font-medium">{p.nome}</span><span className="block text-xs t-mut2">{p.marca} · {p.cat}</span></td>
                        <td className="mono text-xs t-mut">{p.local}</td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <Barra v={p.qtd} max={p.max} tone={tone} />
                            <span className="mono text-sm font-semibold" style={{ width: 46, textAlign: "right", color: `var(--${tone})` }}>{p.qtd} {p.un}</span>
                          </div>
                          <div className="text-xs t-mut2 text-right mt-0.5">mín. {p.min}</div>
                        </td>
                        <td className="num text-sm t-mut">{brl(p.custo)}</td>
                        <td className="num text-sm font-semibold">{brl(p.venda)}</td>
                        {admin && <td className="num text-sm"><span className={margem >= 45 ? "t-ok" : margem >= 30 ? "t-warn" : "t-crit"}>{margem.toFixed(0)}%</span></td>}
                        {/* stopPropagation: sem isto, lançar entrada abria a ficha por baixo */}
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1 justify-end">
                            <Btn variant="o" size="xs" icon={Eye} onClick={() => setVerId(p.id)}>Ver</Btn>
                            <Btn variant="o" size="xs" icon={PackagePlus} onClick={() => abrirMov(p, "entrada")}>Entrada</Btn>
                            <Btn variant="o" size="xs" icon={PackageMinus} onClick={() => abrirMov(p, "saida")}>Saída</Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {lista.length === 0 && <Vazio icon={Package} titulo="Nenhuma peça encontrada" msg="Ajuste os filtros ou cadastre uma peça nova." />}
          </div>
        </>
      ) : aba === "pedidos" ? (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead><tr><th>Pedido</th><th>Fornecedor</th><th>Aberto em</th><th className="num">Itens</th><th className="num">Total</th><th>Situação</th><th></th></tr></thead>
              <tbody>
                {(db.pedidos ?? []).map((pd) => (
                  <tr key={pd.id} onClick={() => abrirPedido(pd)} style={{ cursor: "pointer" }}>
                    <td className="mono text-xs t-mut">#{pd.numero}</td>
                    <td className="text-sm font-medium">{pd.forn}</td>
                    <td className="text-xs t-mut">{fdata(pd.data)}</td>
                    <td className="num text-sm">{pd.itens}</td>
                    <td className="num text-sm font-semibold">{brl(pd.total)}</td>
                    <td><Pill t={PED_TOM[pd.status] ?? "mut"}>{PED_ROT[pd.status] ?? pd.status}</Pill></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Btn variant="o" size="xs" icon={Eye} onClick={() => abrirPedido(pd)}>Ver</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(db.pedidos ?? []).length === 0 && (
            <Vazio icon={Truck} titulo="Nenhum pedido de compra"
              msg="Os pedidos nascem na aba Peças, no botão da sugestão de reposição." />
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead><tr><th>Data</th><th>Peça</th><th>Tipo</th><th className="num">Qtd</th><th>Origem / destino</th><th>Responsável</th><th className="num">Valor unitário</th></tr></thead>
              <tbody>
                {db.mov.slice(0, 60).map((m) => {
                  const p = db.pecas.find((x) => x.id === m.peca);
                  return (
                    <tr key={m.id}>
                      <td className="text-sm t-mut mono">{fdata(m.data)}</td>
                      <td><span className="text-sm font-medium">{p?.nome}</span><span className="block text-xs t-mut2 mono">{p?.cod}</span></td>
                      <td>{m.tipo === "entrada" ? <Pill t="ok" icon={ArrowDown}>Entrada</Pill> : <Pill t="sig" icon={ArrowUp}>Saída</Pill>}</td>
                      <td className="num text-sm font-semibold">{m.qtd}</td>
                      <td className="text-sm t-mut">{m.motivo}</td>
                      <td className="text-sm">{db.usuarios.find((u) => u.id === m.resp)?.nome.split(" ")[0]}</td>
                      <td className="num text-sm">{brl(m.valor)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={!!movModal} onClose={() => setMovModal(null)} w="max-w-lg"
        title={movModal?.tipo === "entrada" ? "Lançar entrada" : "Registrar saída"} sub={movModal?.peca.nome}
        footer={<><Btn variant="g" onClick={() => setMovModal(null)}>Cancelar</Btn>
          <Btn variant={movModal?.tipo === "entrada" ? "ok" : "p"} icon={Save} onClick={gravarMov}>{movModal?.tipo === "entrada" ? "Confirmar entrada" : "Confirmar saída"}</Btn></>}>
        {movModal && (
          <div className="grid gap-3.5">
            <div className="flex items-center gap-3 p-3 bg-el brd" style={{ borderRadius: "var(--r)" }}>
              <Package size={19} className="t-mut" />
              <div className="flex-1"><div className="text-sm font-semibold">{movModal.peca.nome}</div><div className="text-xs t-mut mono">{movModal.peca.cod} · prateleira {movModal.peca.local}</div></div>
              <div className="text-right"><div className="eyebrow">Atual</div><div className="mono font-semibold">{movModal.peca.qtd} {movModal.peca.un}</div></div>
            </div>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Field label={`Quantidade (${movModal.peca.un})`}><input className="inp inp-mono" value={mf.qtd} onChange={(e) => setMf({ ...mf, qtd: e.target.value })} /></Field>
              <Field label={movModal.tipo === "entrada" ? "Custo unitário da compra" : "Custo de referência"} hint={movModal.tipo === "entrada" ? "Recalcula o custo médio." : undefined}>
                <input className="inp inp-mono" value={mf.custo} onChange={(e) => setMf({ ...mf, custo: e.target.value })} disabled={movModal.tipo === "saida"} />
              </Field>
            </div>
            {movModal.tipo === "entrada"
              ? <Field label="Fornecedor / nota de compra"><input className="inp" value={mf.forn} onChange={(e) => setMf({ ...mf, forn: e.target.value })} placeholder="Distribuidora Sul Peças — NF 41190" /></Field>
              : <Field label="Motivo da saída">
                <select className="inp" value={mf.motivo} onChange={(e) => setMf({ ...mf, motivo: e.target.value })}>
                  {["Uso interno", "Venda balcão", "Perda / avaria", "Devolução ao fornecedor", "Ajuste de inventário"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>}
            <div className="flex items-center justify-between p-3" style={{ background: "var(--el)", borderRadius: "var(--r)", border: "1px solid var(--line)" }}>
              <span className="text-sm t-mut">Estoque depois do lançamento</span>
              <span className="mono font-semibold" style={{ fontSize: 17 }}>
                {movModal.tipo === "entrada" ? movModal.peca.qtd + (+mf.qtd || 0) : movModal.peca.qtd - (+mf.qtd || 0)} {movModal.peca.un}
              </span>
            </div>
          </div>
        )}
      </Modal>

      <ImportarEstoque open={importar} onClose={() => setImportar(false)} db={db} set={set} toast={toast} acoes={acoes} />

      {/* ── confirmar geração do pedido ──────────────────────────────── */}
      <Modal open={confirmando} onClose={() => setConfirmando(false)} w="max-w-2xl"
        title="Gerar pedido de compra?"
        sub="Um pedido por fornecedor, em rascunho. Nada é enviado a ninguém."
        footer={<>
          <Btn variant="g" onClick={() => setConfirmando(false)}>Voltar</Btn>
          <Btn variant="p" icon={Send} disabled={ocupado || !grupos.length} onClick={gerarPedido}>
            Gerar {grupos.length} pedido{grupos.length === 1 ? "" : "s"}
          </Btn>
        </>}>
        <div className="grid gap-3">
          {grupos.map((g) => (
            <div key={g.fornecedorId ?? "sem"} className="card" style={{ padding: 12 }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{g.forn}</span>
                <span className="mono text-sm">{brl(g.itens.reduce((t, i) => t + i.comprar * i.custo, 0))}</span>
              </div>
              <table className="tbl">
                <tbody>
                  {g.itens.map((i) => (
                    <tr key={i.id}>
                      <td className="text-xs">{i.nome}<span className="block t-mut2 mono">{i.cod}</span></td>
                      <td className="num text-xs mono">{i.comprar} {i.un}</td>
                      <td className="num text-xs">{brl(i.comprar * i.custo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!g.fornecedorId && (
                <div className="text-xs t-warn mt-2">
                  Estas peças não têm fornecedor padrão. O pedido é criado do mesmo jeito —
                  defina o fornecedor na ficha de cada peça para que da próxima vez elas
                  entrem no pedido certo.
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between text-sm">
            <span className="t-mut">Total estimado, a custo médio</span>
            <span className="mono font-semibold">{brl(totalCompra)}</span>
          </div>
          <p className="text-xs t-mut">
            O estoque não muda agora. A mercadoria entra quando você abrir o pedido e marcar
            como recebido — é aí que a quantidade sobe, o custo médio é recalculado e a conta
            a pagar é gerada.
          </p>
        </div>
      </Modal>

      {/* ── pedido aberto ────────────────────────────────────────────── */}
      <Modal open={!!pedido && !recebendo} onClose={() => setPedido(null)} w="max-w-2xl"
        title={pedido ? `Pedido #${pedido.numero}` : ""}
        sub={pedido ? `${pedido.forn} · aberto em ${fdata(pedido.data)}` : ""}
        footer={pedido && <>
          {pedido.status !== "recebido" && pedido.status !== "cancelado" && (
            <Btn variant="x" disabled={ocupado}
              onClick={() => mudarPedido(pedido, "cancelado", "Pedido cancelado")}>Cancelar pedido</Btn>
          )}
          <div style={{ flex: 1 }} />
          {pedido.status === "rascunho" && (
            <Btn variant="o" icon={Send} disabled={ocupado}
              onClick={() => mudarPedido(pedido, "enviado", "Pedido marcado como enviado")}>Marcar como enviado</Btn>
          )}
          {(pedido.status === "rascunho" || pedido.status === "enviado") && (
            <Btn variant="p" icon={PackagePlus} disabled={ocupado}
              onClick={() => setRecebendo(true)}>Recebi a mercadoria</Btn>
          )}
        </>}>
        {pedido && (
          <div className="grid gap-3">
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
              <Dado r="Situação" v={PED_ROT[pedido.status] ?? pedido.status} />
              <Dado r="Total" v={brl(pedido.total)} />
              {pedido.fornTel && <Dado r="Telefone" v={pedido.fornTel} />}
              {pedido.recebido && <Dado r="Recebido em" v={fdata(pedido.recebido)} />}
            </div>
            <table className="tbl">
              <thead><tr><th>Peça</th><th className="num">Quantidade</th><th className="num">Custo</th><th className="num">Total</th></tr></thead>
              <tbody>
                {(pedido.linhas ?? []).map((i) => (
                  <tr key={i.id}>
                    <td className="text-sm">{i.descricao}<span className="block text-xs t-mut2 mono">{i.codigo_interno}</span></td>
                    <td className="num text-sm mono">{Number(i.quantidade)} {i.unidade}</td>
                    <td className="num text-sm">{brl(Number(i.custo_unitario))}</td>
                    <td className="num text-sm font-semibold">{brl(Number(i.quantidade) * Number(i.custo_unitario))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pedido.status !== "recebido" && (
              <p className="text-xs t-mut">
                Enquanto não for recebido, nada disto está no estoque — é uma intenção de
                compra, não mercadoria.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ── confirmar recebimento ────────────────────────────────────── */}
      <Modal open={recebendo} onClose={() => setRecebendo(false)} w="max-w-lg"
        title="A mercadoria chegou inteira?"
        sub="Receber lança a entrada no estoque e gera a conta a pagar."
        footer={<>
          <Btn variant="g" onClick={() => setRecebendo(false)}>Voltar</Btn>
          <Btn variant="p" icon={PackagePlus} disabled={ocupado || !pedido} onClick={receberPedido}>Confirmar recebimento</Btn>
        </>}>
        {pedido && (
          <div className="text-sm">
            <p className="mb-2"><strong>Pedido #{pedido.numero}</strong> — {pedido.forn} · {brl(pedido.total)}</p>
            <p className="t-mut">
              Cada peça do pedido entra na quantidade pedida, pelo custo do pedido. O custo
              médio é recalculado e uma conta a pagar do valor total é lançada no vencimento
              do fornecedor.
            </p>
            <p className="t-warn mt-2">
              Recebido não volta atrás, e receber duas vezes dobraria o estoque — o servidor
              recusa a segunda. Se veio menos do que foi pedido, receba assim e ajuste pela
              Saída, ou corrija pelo inventário.
            </p>
          </div>
        )}
      </Modal>

      {/* ── ficha da peça ────────────────────────────────────────────── */}
      <Modal open={!!peca && !excluindo} onClose={() => setVerId(null)} w="max-w-2xl"
        title={peca ? peca.nome : ""} sub={peca ? `Código ${peca.cod} · ${peca.cat}` : ""}
        footer={peca && <>
          {admin && <Btn variant="x" icon={Trash2} onClick={() => setExcluindo(true)}>Excluir</Btn>}
          <div style={{ flex: 1 }} />
          <Btn variant="o" icon={PackageMinus} onClick={() => { setVerId(null); abrirMov(peca, "saida"); }}>Saída</Btn>
          <Btn variant="o" icon={PackagePlus} onClick={() => { setVerId(null); abrirMov(peca, "entrada"); }}>Entrada</Btn>
          <Btn variant="p" icon={Pencil} onClick={() => { setVerId(null); abrirEdicao(peca); }}>Editar</Btn>
        </>}>
        {peca && (() => {
          const reservado = peca.fisico - peca.qtd;
          const margem = peca.venda ? ((peca.venda - peca.custo) / peca.venda) * 100 : 0;
          const tone = peca.qtd === 0 ? "crit" : peca.qtd <= peca.min ? "signal" : "ok";
          return (
            <div className="grid gap-4">
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
                <Dado r="Na prateleira" v={`${peca.fisico} ${peca.un}`} />
                <Dado r="Reservado em OS" v={`${reservado} ${peca.un}`}
                  hint={reservado > 0 ? "Já prometido a uma OS aberta." : undefined} />
                <Dado r="Disponível para vender" v={`${peca.qtd} ${peca.un}`} tone={tone} />
                <Dado r="Mínimo / máximo" v={`${peca.min} / ${peca.max || "—"}`} />
              </div>

              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
                <Dado r="Custo médio" v={brl(peca.custo)} hint="Recalculado a cada entrada." />
                <Dado r="Preço de venda" v={brl(peca.venda)} />
                {admin && <Dado r="Margem" v={`${margem.toFixed(0)}%`}
                  tone={margem >= 45 ? "ok" : margem >= 30 ? "warn" : "crit"} />}
                <Dado r="Parado na prateleira" v={brl(peca.fisico * peca.custo)} />
              </div>

              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
                <Dado r="Marca" v={peca.marca || "—"} />
                <Dado r="Unidade" v={peca.un} />
                <Dado r="Prateleira" v={peca.local || "—"} />
                <Dado r="Fornecedor" v={peca.forn || "—"} />
                <Dado r="NCM" v={peca.ncm || "—"} hint={!peca.ncm ? "Sem NCM a nota é rejeitada." : undefined} />
              </div>

              {(() => {
                const meus = db.mov.filter((m) => m.peca === peca.id).slice(0, 5);
                return meus.length > 0 && (
                  <div>
                    <div className="lbl mb-1">Últimos movimentos</div>
                    <table className="tbl">
                      <tbody>
                        {meus.map((m, i) => (
                          <tr key={m.id ?? i}>
                            <td className="text-xs t-mut">{fdata(m.data)}</td>
                            <td className="text-xs">{m.motivo || (m.tipo === "entrada" ? "Entrada" : "Saída")}</td>
                            <td className="num text-xs mono"
                              style={{ color: `var(--${m.tipo === "entrada" ? "ok" : "crit"})` }}>
                              {m.tipo === "entrada" ? "+" : "−"}{m.qtd}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </Modal>

      {/* ── excluir ──────────────────────────────────────────────────── */}
      <Modal open={excluindo} onClose={() => setExcluindo(false)} w="max-w-lg"
        title="Excluir esta peça do cadastro?"
        sub="Ela sai da lista e dos orçamentos. O histórico de quem já a usou fica de pé."
        footer={<>
          <Btn variant="g" onClick={() => setExcluindo(false)}>Voltar</Btn>
          <Btn variant="x" icon={Trash2} disabled={ocupado || !peca} onClick={confirmarExclusaoPeca}>Excluir</Btn>
        </>}>
        {peca && (
          <div className="text-sm">
            <p className="mb-2"><strong>{peca.cod}</strong> — {peca.nome}</p>
            <p className="t-mut">
              A peça é desativada, não apagada: as OS e notas que já a usaram continuam
              mostrando o que foi vendido. É por isso que ela some da busca mas o histórico
              não muda.
            </p>
            {peca.fisico > 0 && (
              <p className="t-warn mt-2">
                Ainda há <strong>{peca.fisico} {peca.un}</strong> na prateleira. Excluindo agora,
                esse valor ({brl(peca.fisico * peca.custo)}) sai do capital parado sem ter saído
                do estoque de verdade. Se a peça acabou mesmo, dê baixa antes pela Saída.
              </p>
            )}
            {peca.fisico - peca.qtd > 0 && (
              <p className="t-crit mt-2">
                Há {peca.fisico - peca.qtd} {peca.un} reservados numa OS aberta.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal open={nova || !!editando} onClose={fecharForm}
        title={editando ? "Editar peça" : "Nova peça no estoque"}
        sub={editando
          ? "Quantidade e custo médio não se mudam aqui: os dois vêm do razão de movimentos."
          : "Defina o mínimo com cuidado: é ele que dispara o alerta de reposição."}
        footer={<><Btn variant="g" onClick={fecharForm}>Cancelar</Btn><Btn variant="p" icon={Save} disabled={ocupado} onClick={salvarPeca}>{editando ? "Salvar alterações" : "Cadastrar peça"}</Btn></>}>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))" }}>
          <Field label="Código interno"><input className="inp inp-mono" value={pf.cod} onChange={(e) => setPf({ ...pf, cod: e.target.value.toUpperCase() })} placeholder="FLT-0000" /></Field>
          <Field label="Nome da peça" className="col-span-full"><input className="inp" value={pf.nome} onChange={(e) => setPf({ ...pf, nome: e.target.value })} placeholder="Filtro de óleo do motor" /></Field>
          <Field label="Categoria" className="col-span-full"
            hint={(db.categorias ?? []).length === 0
              ? "Nenhuma categoria ainda. Crie a primeira no campo ao lado — ex.: Filtros, Freios, Suspensão."
              : "Serve para agrupar e filtrar o estoque."}>
            <div className="flex gap-2">
              <select className="inp" style={{ flex: 1 }} value={pf.catId}
                onChange={(e) => setPf({ ...pf, catId: e.target.value })}>
                <option value="">Sem categoria</option>
                {(db.categorias ?? []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              <input className="inp" style={{ flex: 1 }} placeholder="Criar categoria nova…"
                value={novaCat} onChange={(e) => setNovaCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); criarCategoria(); } }} />
              <Btn variant="o" icon={Plus} disabled={criandoCat || !novaCat.trim()}
                onClick={criarCategoria}>Criar</Btn>
            </div>
          </Field>
          <Field label="Marca"><input className="inp" value={pf.marca} onChange={(e) => setPf({ ...pf, marca: e.target.value })} /></Field>
          <Field label="Unidade"><select className="inp" value={pf.un} onChange={(e) => setPf({ ...pf, un: e.target.value })}>{["UN", "PC", "JG", "KIT", "LT", "FR", "MT"].map((u) => <option key={u}>{u}</option>)}</select></Field>
          <Field label="Prateleira"><input className="inp inp-mono" value={pf.local} onChange={(e) => setPf({ ...pf, local: e.target.value.toUpperCase() })} placeholder="A-01" /></Field>
          {!editando && <Field label="Quantidade inicial" hint="Vira uma entrada no razão."><input className="inp inp-mono" value={pf.qtd} onChange={(e) => setPf({ ...pf, qtd: e.target.value })} /></Field>}
          <Field label="Estoque mínimo" hint="Dispara o alerta."><input className="inp inp-mono" value={pf.min} onChange={(e) => setPf({ ...pf, min: e.target.value })} /></Field>
          <Field label="Estoque máximo" hint="Usado na sugestão de compra."><input className="inp inp-mono" value={pf.max} onChange={(e) => setPf({ ...pf, max: e.target.value })} /></Field>
          <Field label={editando ? "Custo médio" : "Custo unitário"}
            hint={editando ? "Só muda com entrada de mercadoria." : undefined}>
            <input className="inp inp-mono" value={pf.custo} disabled={!!editando}
              onChange={(e) => setPf({ ...pf, custo: e.target.value })} /></Field>
          <Field label="Preço de venda" hint={pf.venda && pf.custo ? `Margem de ${(((pf.venda - pf.custo) / pf.venda) * 100).toFixed(0)}%` : undefined}>
            <input className="inp inp-mono" value={pf.venda} onChange={(e) => setPf({ ...pf, venda: e.target.value })} /></Field>
          <Field label="Fornecedor padrão" className="col-span-full"
            hint="É por ele que o pedido de compra é agrupado — um pedido por fornecedor.">
            <div className="flex gap-2">
              <select className="inp" style={{ flex: 1 }} value={pf.fornId}
                onChange={(e) => setPf({ ...pf, fornId: e.target.value })}>
                <option value="">Sem fornecedor</option>
                {(db.fornecedores ?? []).map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
              <input className="inp" style={{ flex: 1 }} placeholder="Criar fornecedor novo…"
                value={novoForn} onChange={(e) => setNovoForn(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); criarFornecedor(); } }} />
              <Btn variant="o" icon={Plus} disabled={criandoForn || !novoForn.trim()}
                onClick={criarFornecedor}>Criar</Btn>
            </div>
          </Field>
          <Field label="NCM" className="col-span-full"
            hint="Oito dígitos. Sem NCM a NF-e da peça é rejeitada na SEFAZ.">
            <input className="inp inp-mono" value={pf.ncm} maxLength={8}
              onChange={(e) => setPf({ ...pf, ncm: e.target.value.replace(/\D/g, "") })}
              placeholder="84212300" /></Field>
        </div>
      </Modal>
    </div>
  );
}

export { Estoque };
